/**
 * SeamlessSlot cross-origin tracking bridge (iframe -> host page).
 *
 * The booking app runs in a cross-origin iframe, so the host site's Cloudflare
 * Zaraz / GTM cannot see anything that happens in here. This module posts
 * structured events up to the host, where script.js replays them into
 * zaraz.track().
 *
 * Notes
 *  - parentOrigin is resolved once and cached in memory, so it survives SPA
 *    route changes (/appointment -> /patientInfo -> /confirmation) even though
 *    the query string is rewritten. sessionStorage is the fallback for hard
 *    reloads. localStorage is deliberately NOT used because
 *    Appointment.loadAllLocations() calls localStorage.clear().
 *  - Payloads are scrubbed of obvious PII before leaving the frame. Never send
 *    patient name / email / phone / notes into a client's analytics stack.
 */

const SOURCE = 'seamlessslot';
const HOST_SOURCE = 'seamlessslot-host';
const VERSION = 1;

const ORIGIN_KEY = 'ss_parent_origin';
const HOST_CTX_KEY = 'ss_host_context';
const ORIGIN_RE = /^https?:\/\/[a-z0-9.-]+(:\d+)?$/i;

let parentOrigin = null;
let hostContext = null;
let listenerBound = false;
const onceSent = new Set();

const inIframe = (() => {
  try {
    return window.parent !== window;
  } catch (e) {
    return true; // cross-origin access threw, so we are framed
  }
})();

function readSession(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null; // storage can be blocked in partitioned third-party frames
  }
}

function writeSession(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    /* no-op */
  }
}

function isValidOrigin(origin) {
  return typeof origin === 'string' && ORIGIN_RE.test(origin);
}

function resolveParentOrigin() {
  if (isValidOrigin(parentOrigin)) return parentOrigin;

  // 1. explicit param, present on the first load only
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('parentOrigin');
    if (isValidOrigin(fromQuery)) {
      parentOrigin = fromQuery;
      writeSession(ORIGIN_KEY, fromQuery);
      return parentOrigin;
    }
  } catch (e) {
    /* no-op */
  }

  // 2. cached from an earlier page in this session
  const stored = readSession(ORIGIN_KEY);
  if (isValidOrigin(stored)) {
    parentOrigin = stored;
    return parentOrigin;
  }

  // 3. last resort: the embedding page is our referrer
  try {
    if (document.referrer) {
      const refOrigin = new URL(document.referrer).origin;
      if (isValidOrigin(refOrigin) && refOrigin !== window.location.origin) {
        parentOrigin = refOrigin;
        writeSession(ORIGIN_KEY, refOrigin);
        return parentOrigin;
      }
    }
  } catch (e) {
    /* no-op */
  }

  return null;
}

// Pages like /patientInfo (reached directly from an SMS/email payment or
// reschedule link) carry no plain ?locationId= - it's packed inside the
// base64 "query" blob instead. Decode it from there before ever falling
// back to sessionStorage, since that link can be opened on a device/session
// that never visited /appointment (or that still has a stale loc_id from
// an unrelated earlier booking).
function locationIdFromEncodedQuery() {
  try {
    const encoded = new URLSearchParams(window.location.search).get('query');
    if (!encoded) return null;
    return new URLSearchParams(atob(encoded)).get('locationId') || null;
  } catch (e) {
    return null;
  }
}

function currentLocationId() {
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('locationId');
    if (fromQuery) return fromQuery;
    const fromEncodedQuery = locationIdFromEncodedQuery();
    if (fromEncodedQuery) return fromEncodedQuery;
    return sessionStorage.getItem('loc_id') || null;
  } catch (e) {
    return null;
  }
}

// Anything matching this never leaves the iframe.
const PII_KEY_RE =
  /(name|email|phone|mobile|address|postcode|zip|comment|note|dob|birth|password|nhs|patient)/i;

function scrub(payload) {
  const out = {};
  if (!payload) return out;
  Object.keys(payload).forEach((key) => {
    if (PII_KEY_RE.test(key)) return;
    const value = payload[key];
    if (value === null || value === undefined || value === '') return;
    // Zaraz expects a flat object of primitives
    out[key] = typeof value === 'object' ? JSON.stringify(value) : value;
  });
  return out;
}

/**
 * Call once, as early as possible (App root or Appointment mount).
 * Binds the host-context listener and announces readiness.
 */
export function initTrackingBridge() {
  if (!inIframe || listenerBound) return;
  listenerBound = true;

  resolveParentOrigin();

  window.addEventListener('message', (event) => {
    if (!parentOrigin || event.origin !== parentOrigin) return;
    const data = event.data;
    if (!data || data.source !== HOST_SOURCE) return;
    if (data.event !== 'host_context') return;
    hostContext = data.payload || {};
    writeSession(HOST_CTX_KEY, JSON.stringify(hostContext));
  });

  // ask the host page for its URL / utm / click ids
  post('ready', {});
}

function post(event, payload) {
  if (!inIframe || !event) return;
  if (!resolveParentOrigin()) return;

  try {
    window.parent.postMessage(
      {
        source: SOURCE,
        version: VERSION,
        event,
        locationId: currentLocationId(),
        path: window.location.pathname,
        payload: scrub(payload),
        ts: Date.now()
      },
      parentOrigin
    );
  } catch (e) {
    /* never let tracking break the booking flow */
  }
}

/** Emit an event to the host page. Safe to call when not framed (no-op). */
export function emit(event, payload = {}) {
  post(event, payload);
}

/** Emit at most once per page-load / session of this module instance. */
export function emitOnce(event, payload = {}) {
  if (onceSent.has(event)) return;
  onceSent.add(event);
  post(event, payload);
}

/**
 * utm / gclid / fbclid / host URL captured from the *parent* page.
 * Use this instead of reading window.location.search, which only ever
 * contains the iframe's own params.
 */
export function getHostContext() {
  if (hostContext) return hostContext;
  const raw = readSession(HOST_CTX_KEY);
  if (raw) {
    try {
      hostContext = JSON.parse(raw);
    } catch (e) {
      hostContext = null;
    }
  }
  return hostContext || {};
}

export function isEmbedded() {
  return inIframe;
}
